import {
  CheckCircle as CheckCircleIcon,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';

/**
 * Format a date string to a readable format
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date string
 */
export const formatDate = dateString => {
  if (!dateString) return 'N/A';

  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    return 'Invalid Date';
  }
};

/**
 * Get the color for experiment status
 * @param {string} status - Experiment status
 * @returns {string} Material-UI color
 */
export const getStatusColor = status => {
  const statusColors = {
    draft: 'default',
    active: 'success',
    paused: 'warning',
    completed: 'info',
    cancelled: 'error',
  };

  return statusColors[status] || 'default';
};

/**
 * Get the icon for experiment status
 * @param {string} status - Experiment status
 * @returns {React.Component} Material-UI icon component
 */
export const getStatusIcon = status => {
  const statusIcons = {
    draft: ScheduleIcon,
    active: PlayArrowIcon,
    paused: PauseIcon,
    completed: CheckCircleIcon,
    cancelled: CancelIcon,
  };

  const IconComponent = statusIcons[status] || ScheduleIcon;
  return <IconComponent />;
};

/**
 * Calculate the duration between two dates
 * @param {string} startDate - Start date string
 * @param {string} endDate - End date string (optional)
 * @returns {string} Duration string
 */
export const calculateDuration = (startDate, endDate = null) => {
  if (!startDate) return 'N/A';

  try {
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date();
    const diffMs = end - start;

    if (diffMs < 0) return '0 days';

    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(
      (diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );

    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ${diffHours}h`;
    } else {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''}`;
    }
  } catch (error) {
    return 'N/A';
  }
};

/**
 * Format traffic allocation for display
 * @param {string|object} trafficAllocation - Traffic allocation value
 * @returns {string} Formatted string
 */
export const formatTrafficAllocation = trafficAllocation => {
  if (typeof trafficAllocation === 'string') {
    return trafficAllocation.replace('_', ' ').toUpperCase();
  }

  if (typeof trafficAllocation === 'object') {
    const entries = Object.entries(trafficAllocation);
    return entries
      .map(([name, percentage]) => `${name}: ${percentage}%`)
      .join(', ');
  }

  return 'Unknown';
};

/**
 * Validate experiment data
 * @param {object} experimentData - Experiment data to validate
 * @returns {object} Validation result with isValid and errors
 */
export const validateExperimentData = experimentData => {
  const errors = {};

  // Required fields
  if (!experimentData.name?.trim()) {
    errors.name = 'Experiment name is required';
  }

  if (!experimentData.template_id) {
    errors.template_id = 'Template selection is required';
  }

  if (!experimentData.treatments || experimentData.treatments.length < 2) {
    errors.treatments = 'At least 2 treatments are required';
  }

  // Validate treatments
  if (experimentData.treatments) {
    experimentData.treatments.forEach((treatment, index) => {
      if (!treatment.name?.trim()) {
        errors[`treatment_${index}_name`] = 'Treatment name is required';
      }

      if (!treatment.treatment_type) {
        errors[`treatment_${index}_type`] = 'Treatment type is required';
      }
    });
  }

  // Validate numeric fields
  if (
    experimentData.minimum_sample_size &&
    experimentData.minimum_sample_size < 10
  ) {
    errors.minimum_sample_size = 'Minimum sample size must be at least 10';
  }

  if (
    experimentData.statistical_significance &&
    (experimentData.statistical_significance < 0.8 ||
      experimentData.statistical_significance > 0.99)
  ) {
    errors.statistical_significance =
      'Statistical significance must be between 0.8 and 0.99';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

/**
 * Generate a unique treatment name
 * @param {string} baseName - Base name for the treatment
 * @param {array} existingTreatments - Array of existing treatments
 * @returns {string} Unique treatment name
 */
export const generateUniqueTreatmentName = (baseName, existingTreatments) => {
  const existingNames = existingTreatments.map(t => t.name.toLowerCase());
  let counter = 1;
  let uniqueName = baseName;

  while (existingNames.includes(uniqueName.toLowerCase())) {
    uniqueName = `${baseName} ${counter}`;
    counter++;
  }

  return uniqueName;
};

/**
 * Get experiment status display text
 * @param {string} status - Experiment status
 * @returns {string} Display text
 */
export const getStatusDisplayText = status => {
  const statusTexts = {
    draft: 'Draft',
    active: 'Active',
    paused: 'Paused',
    completed: 'Completed',
    cancelled: 'Cancelled',
  };

  return statusTexts[status] || status;
};

/**
 * Check if experiment can be started
 * @param {object} experiment - Experiment object
 * @returns {boolean} Whether experiment can be started
 */
export const canStartExperiment = experiment => {
  return experiment.status === 'draft' || experiment.status === 'paused';
};

/**
 * Check if experiment can be paused
 * @param {object} experiment - Experiment object
 * @returns {boolean} Whether experiment can be paused
 */
export const canPauseExperiment = experiment => {
  return experiment.status === 'active';
};

/**
 * Check if experiment can be stopped
 * @param {object} experiment - Experiment object
 * @returns {boolean} Whether experiment can be stopped
 */
export const canStopExperiment = experiment => {
  return experiment.status === 'active' || experiment.status === 'paused';
};

/**
 * Get experiment progress percentage
 * @param {object} experiment - Experiment object
 * @returns {number} Progress percentage (0-100)
 */
export const getExperimentProgress = experiment => {
  if (!experiment.start_date) return 0;

  const startDate = new Date(experiment.start_date);
  const endDate = experiment.end_date
    ? new Date(experiment.end_date)
    : new Date();
  const now = new Date();

  if (now < startDate) return 0;
  if (now > endDate) return 100;

  const totalDuration = endDate - startDate;
  const elapsed = now - startDate;

  return Math.min(100, Math.max(0, (elapsed / totalDuration) * 100));
};
